import {
  EventJsonldEventLocatorReadShopLocatorRead,
  EventJsonldEventReadEventFormatReadIllustrationRead,
} from '@/api/generated';
import AvatarLabel from '@/atoms/AvatarLabel';
import Chip from '@/atoms/Chip';
import clx from 'classnames';
import { STATUS_TO_CHIP } from 'constants/eventsConstants';
import { parseISO } from 'date-fns';
import { useDateFnsWithLocale } from 'hooks/i18n';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { SIZE, VARIANT_COLOR_ENUM } from 'types/utils';
import { CMSEventType } from 'utils/cms/types';
import { ColorToken } from 'utils/colors';
import { localizedDistanceUnit } from 'utils/distances';
import {
  EVENT_TYPE_COLOR_TO_VARIANT,
  getEventFormatIntlKey,
  getEventGameFormatIntlKey,
  getEventStatusIntlKey,
  getEventTypeIntlKey,
} from 'utils/events';

const centerIton = 'min-w-5 text-center';

type Props = {
  event: EventJsonldEventReadEventFormatReadIllustrationRead | EventJsonldEventLocatorReadShopLocatorRead;
  href: string;
  displayDistance?: boolean;
  eventType?: CMSEventType;
};

const EventTile = ({ event, href, displayDistance, eventType }: Props) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { localeFormat } = useDateFnsWithLocale();

  return (
    <Link className="hover:no-underline" href={href}>
      <div className="rounded-t-sm bg-mercury-50">
        <div
          className="h-32 bg-no-repeat bg-cover bg-center rounded-t-sm"
          style={{
            backgroundImage: `url(${event.imagePath || 'https://altered-prod-eu.s3.amazonaws.com/altered_roadshow_country_nl.jpg'})`,
          }}
        >
          <div className="bg-black/40 relative h-full w-full rounded-t-sm">
            <ul className="flex w-fit shrink-1 flex-wrap gap-2 p-2">
              {eventType && eventType.reference !== 'EXPEDITION' && (
                <li>
                  <Chip color={EVENT_TYPE_COLOR_TO_VARIANT[eventType?.color as ColorToken]} size={SIZE.SM}>
                    {t(getEventTypeIntlKey(eventType.reference))}
                  </Chip>
                </li>
              )}
              {event?.eventFormat && (
                <li>
                  <Chip color={VARIANT_COLOR_ENUM.NEUTRAL} size={SIZE.SM}>
                    {t(getEventFormatIntlKey(event.eventFormat))}
                  </Chip>
                </li>
              )}
              {event?.gameFormat && (
                <li>
                  <Chip color={VARIANT_COLOR_ENUM.NEUTRAL} size={SIZE.SM}>
                    {t(getEventGameFormatIntlKey(event.gameFormat))}
                  </Chip>
                </li>
              )}
              {event.eventUser && (
                <li>
                  <Chip size={SIZE.SM}>{t(`event.user.status.${event.eventUser.status?.toLowerCase()}` || '')}</Chip>
                </li>
              )}
              {event.status && event.status !== 'PUBLISHED' && (
                <li>
                  <Chip color={STATUS_TO_CHIP[event.status]} size={SIZE.SM}>
                    {t(getEventStatusIntlKey(event.status))}
                  </Chip>
                </li>
              )}
              {event.distance && displayDistance ? (
                <li>
                  <Chip color={VARIANT_COLOR_ENUM.NEUTRAL_DARK} size={SIZE.SM}>
                    {localizedDistanceUnit(event.distance, language)}
                  </Chip>
                </li>
              ) : null}
            </ul>
            <p className="absolute left-2 bottom-2 title-5 text-neutral-50 line-clamp-1">{event.name}</p>
          </div>
        </div>
        <div className="flex flex-col gap-1 text-neutral-800 p-4 bg-mercury-100">
          {event.organizer?.shop && typeof event.organizer.shop !== 'string' && 'name' in event.organizer.shop && (
            <AvatarLabel avatarPath={event.organizer?.avatarPath} nickName={event.organizer?.shop.name} />
          )}
          <p className="flex flex-row items-center gap-2">
            <i className={clx(centerIton, 'fa-solid fa-fw fa-calendar')} />
            {event.startDateTime && localeFormat(parseISO(event.startDateTime || ''))}
          </p>
          <p className="flex flex-row items-center gap-2">
            <i className={clx(centerIton, 'fa-solid fa-location-dot')} />
            {event.address?.street && event.address?.street}
            {event.address?.city && `, ${event.address?.city}`}
          </p>
          {!!event.placeNumber && event.placeNumber > 0 && (
            <>
              <p className="flex flex-row items-center gap-2">
                <i className={clx(centerIton, 'fa-sharp fa-solid fa-people-group')} />
                {event.placeNumber} {t('places')}
              </p>
              <p className="flex flex-row items-center gap-2">
                <i className={clx(centerIton, 'fa-solid fa-coins')} />
                {t(event.isFree ? 'event.list.free' : 'event.list.paid')}
              </p>
            </>
          )}
        </div>
      </div>
    </Link>
  );
};

export default EventTile;
